/* eslint-disable */
// @ts-nocheck
/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import * as React from 'react';
import { AtlBrowserStorageLocal } from '@atlassian/browser-storage-controls';
import { css, jsx } from '@atlaskit/css';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import InlineMessage from '@atlaskit/inline-message';
import SectionMessage, { SectionMessageAction } from '@atlaskit/section-message';
import { token } from '@atlaskit/tokens';
import {
	FormattedMessage,
	RawIntlProvider,
	createIntl,
	createIntlCache,
	injectIntl,
} from 'react-intl';
import { logger } from '../../adaptors/logger/LoggerAdaptor';
import { connectEosBannerMessages } from './messages';

const DYNAMIC_CONFIG_DISMISS_DAYS = 'customer-messaging-iframe-banner-dismiss-days';
const DISMISS_STORAGE_KEY_SUFFIX = 'connect-eos-banner_dismissed';
const BSC_SUCCESS_STATUS = 'SUCCESS';
const BSC_BLOCKED_STATUS = 'BLOCKED';
const DEFAULT_DISMISS_DAYS = 30;
const MS_PER_DAY: number = 24 * 60 * 60 * 1000;

const FEATURE_GATE_BANNER_ENABLED = 'customer-messaging-iframe-banner-enabled';
const FEATURE_GATE_DEV_CANARY_ENABLED = 'customer-messaging-is-dev-canary';
const DEVELOPER_CANARY_PROGRAM_URL =
	'https://developer.atlassian.com/cloud/jira/platform/developer-canary-program/';
const FORGE_CHANGELOG_URL = 'https://developer.atlassian.com/platform/forge/changelog/';
const DEFAULT_LOCALE = 'en';
const defaultIntlCache = createIntlCache();
const defaultIntl = createIntl({ locale: DEFAULT_LOCALE }, defaultIntlCache);

interface ConnectEosBannerProps {
	appKey?: string;
	appName?: string;
	cloudId?: string;
	isSiteAdmin?: boolean;
	isSystemApp?: boolean;
	moduleType?: string;
}

interface ConnectEosBannerState {
	dismissed: boolean;
}

const containerStyles: React.CSSProperties = {
	marginBottom: '8px',
};

const devCanaryMessageStyles = css({
	marginTop: token('space.0'),
});

function getDismissStorageKey(appKey: string): string {
	return `${appKey}-${DISMISS_STORAGE_KEY_SUFFIX}`;
}

class ConnectEosBannerComponent extends React.PureComponent<
	ConnectEosBannerProps,
	ConnectEosBannerState
> {
	static defaultProps = {
		isSystemApp: false,
		moduleType: '',
	};

	constructor(props: ConnectEosBannerProps) {
		super(props);
		this.state = {
			dismissed: this._isDismissed(),
		};
	}

	_getDismissDurationMs(): number {
		const days = FeatureGates.getExperiment(DYNAMIC_CONFIG_DISMISS_DAYS, {
			fireExperimentExposure: false,
		}).get('value', DEFAULT_DISMISS_DAYS);
		return (typeof days === 'number' && days > 0 ? days : DEFAULT_DISMISS_DAYS) * MS_PER_DAY;
	}

	_isDismissed(): boolean {
		if (typeof window === 'undefined') {
			return false;
		}

		const dismissStorageKey = getDismissStorageKey(this.props.appKey);
		const storedItem = AtlBrowserStorageLocal.getItem(dismissStorageKey);
		if (!storedItem) {
			return false;
		}

		let dismissedAt: number | string | undefined;
		try {
			const parsedItem = JSON.parse(storedItem);
			dismissedAt = parsedItem.value;
		} catch (e) {
			logger.warn('Failed to parse Connect EOS banner dismissal', {
				key: dismissStorageKey,
				error: e,
			});
			return false;
		}

		if (!dismissedAt) {
			return false;
		}
		const dismissedTimestamp = Number(dismissedAt);
		if (isNaN(dismissedTimestamp)) {
			return false;
		}

		return Date.now() - dismissedTimestamp < this._getDismissDurationMs();
	}

	_dismiss = (): void => {
		const dismissedAt = Date.now();
		const storedItem = JSON.stringify({
			value: dismissedAt,
		});
		const dismissStorageKey = getDismissStorageKey(this.props.appKey);

		// @browser-storage setter
		void AtlBrowserStorageLocal.setItem(dismissStorageKey, storedItem)
			.then((result) => {
				if (
					result?.status &&
					result.status !== BSC_SUCCESS_STATUS &&
					result.status !== BSC_BLOCKED_STATUS
				) {
					logger.warn('Failed to persist Connect EOS banner dismissal', {
						key: dismissStorageKey,
						result,
					});
				}
			})
			.catch((error) => {
				logger.warn('Failed to persist Connect EOS banner dismissal', {
					key: dismissStorageKey,
					error,
				});
			});
		this.setState({ dismissed: true });
	};

	_shouldShowBanner(): boolean {
		// Don't show if the main feature gate is not enabled
		if (!FeatureGates.checkGate(FEATURE_GATE_BANNER_ENABLED)) {
			return false;
		}

		// Don't show for system apps
		if (this.props.isSystemApp) {
			return false;
		}

		// Don't show if previously dismissed (within configured dismiss duration)
		if (this.state.dismissed) {
			return false;
		}

		return true;
	}

	_isProd(): boolean {
		if (typeof window === 'undefined') {
			return false;
		}

		return window.location.hostname.endsWith('.atlassian.net');
	}

	_isSiteAdmin(): boolean {
		return this.props.isSiteAdmin ?? false;
	}

	_isDevCanary(): boolean {
		return FeatureGates.checkGate(FEATURE_GATE_DEV_CANARY_ENABLED);
	}

	_getConnectedAppsUrl(): string | undefined {
		const cloudId =
			this.props.cloudId ??
			(typeof document !== 'undefined'
				? (document.querySelector('meta[name="ajs-cloud-id"]') as HTMLMetaElement)?.content
				: undefined) ??
			undefined;
		const adminHost = this._isProd() ? 'admin.atlassian.com' : 'admin.stg.atlassian.com';

		if (!cloudId) {
			return undefined;
		}
		return `https://${adminHost}/s/${cloudId}/user-connected-apps/tab/installed`;
	}

	_getAdminMessageBody(): React.ReactElement<any> | null {
		const { appName } = this.props;
		const connectedAppsUrl = this._getConnectedAppsUrl();
		const message = appName
			? connectEosBannerMessages.adminMessageWithAppName
			: connectEosBannerMessages.adminMessageWithoutAppName;
		const connectedApps = (chunks: React.ReactNode[]) => (
			<strong>
				{connectedAppsUrl ? <a href={connectedAppsUrl}>{chunks}</a> : <span>{chunks}</span>}
			</strong>
		);
		const values = appName
			? { appName: <strong>{appName}</strong>, connectedApps }
			: { connectedApps };

		return (
			<p>
				<FormattedMessage {...message} values={values} />
			</p>
		);
	}

	_getNonAdminMessageBody(): React.ReactElement<any> | null {
		const { appName } = this.props;
		const message = appName
			? connectEosBannerMessages.nonAdminMessageWithAppName
			: connectEosBannerMessages.nonAdminMessageWithoutAppName;

		return (
			<p>
				<FormattedMessage
					{...message}
					values={appName ? { appName: <strong>{appName}</strong> } : {}}
				/>
			</p>
		);
	}

	_getDevCanaryMessageBody(): React.ReactElement<any> | null {
		if (!this._isDevCanary()) {
			return null;
		}

		const developerCanaryProgram = (chunks: React.ReactNode[]) => (
			<a href={DEVELOPER_CANARY_PROGRAM_URL} target="_blank" rel="noopener noreferrer">
				{chunks}
			</a>
		);
		const forgeChangelog = (chunks: React.ReactNode[]) => (
			<a href={FORGE_CHANGELOG_URL} target="_blank" rel="noopener noreferrer">
				{chunks}
			</a>
		);

		return (
			<p css={devCanaryMessageStyles}>
				<FormattedMessage
					{...connectEosBannerMessages.devCanaryMessage}
					values={{ developerCanaryProgram, forgeChangelog }}
				/>
			</p>
		);
	}

	_getDismissAction(): React.ReactElement<any> {
		return (
			<SectionMessageAction onClick={this._dismiss} key="dismiss">
				<FormattedMessage {...connectEosBannerMessages.dismiss} />
			</SectionMessageAction>
		);
	}

	getSectionWarningBanner(): React.ReactElement<any> | null {
		return (
			<div data-testid="connect-eos-banner" style={containerStyles}>
				<SectionMessage appearance="warning">
					{this._isSiteAdmin() ? this._getAdminMessageBody() : this._getNonAdminMessageBody()}
					{this._getDevCanaryMessageBody()}
					{this._getDismissAction()}
				</SectionMessage>
			</div>
		);
	}

	getInlineBanner(): React.ReactElement<any> | null {
		return (
			<div data-testid="connect-eos-v2-banner">
				<InlineMessage
					appearance="warning"
					secondaryText={(this.props.intl ?? defaultIntl).formatMessage(
						connectEosBannerMessages.inlineSecondaryText,
					)}
				>
					{this._isSiteAdmin() ? this._getAdminMessageBody() : this._getNonAdminMessageBody()}
					{this._getDevCanaryMessageBody()}
					{this._getDismissAction()}
				</InlineMessage>
			</div>
		);
	}

	render(): React.ReactElement<any> | null {
		if (!this._shouldShowBanner()) {
			return null;
		}

		const banner =
			this.props.moduleType === 'webPanels'
				? this.getInlineBanner()
				: this.getSectionWarningBanner();

		if (!this.props.intl) {
			return <RawIntlProvider value={defaultIntl}>{banner}</RawIntlProvider>;
		}

		return banner;
	}
}

const ConnectEosBanner: React.ComponentType<ConnectEosBannerProps> = injectIntl(
	ConnectEosBannerComponent,
	{ enforceContext: false },
);

export {
	ConnectEosBanner,
	DISMISS_STORAGE_KEY_SUFFIX,
	DEFAULT_DISMISS_DAYS,
	DYNAMIC_CONFIG_DISMISS_DAYS,
	MS_PER_DAY,
	FEATURE_GATE_BANNER_ENABLED,
	getDismissStorageKey,
};
